import { useId } from "react";

/**
 * A row of filter chips, and the sentence that has to sit under them (issues #329, #310).
 *
 * `Memory.dc.html` draws one such row (*Whose:*) and `Backlog.dc.html` draws two side by side
 * (*Open / Finished / Everything*, then *Whose:*), which is what made this a component rather
 * than markup inside one screen. The chips carry the same geometry, the same selected fill and
 * the same disabled treatment on both, so a second copy would be a second answer to *"what does
 * a frozen filter look like"*.
 *
 * **This module owns the control and none of the meaning.** Which chips exist, what they are
 * called and what each one admits are the screen's — see `WhoseFilter.tsx` and `PlateFilters.tsx`.
 */

/**
 * The selected chip.
 *
 * A plain string rather than a union with a payload, because the vocabularies cannot collide:
 * a `userId` is a ULID (`[0-9A-Z]{26}`, #119) and every literal either screen uses is
 * lowercase.
 */
export type ChipValue = string;

export interface ChipOption {
  readonly value: ChipValue;
  readonly label: string;
  /** Rendered in the muted, dashed treatment the canvas gives a chip that is not a person. */
  readonly faint?: boolean;
}

/**
 * One named group of chips.
 *
 * **The label is a `<span>` the group points at, not a `<legend>`** — and the first version of
 * this was a `<fieldset>`, on the reasoning that *"`display: flex` makes the legend an ordinary
 * flex item rather than a notch in a border there is none of"*. That is not true, and a
 * screenshot is what showed it: **a fieldset's first `<legend>` is its rendered legend**, which
 * the HTML rendering spec lays out over the top border edge rather than as a child box, flex
 * container or not. So *Whose:* sat on its own line above its chips and made the second group
 * two lines tall, which on the list screen — where two groups sit side by side — tipped the
 * whole row out of alignment. The memory screen had it too and nobody had looked.
 *
 * **What the fieldset was for is kept**, which is the part worth not losing: the canvas's
 * visible *Whose:* is still the group's accessible name, through `aria-labelledby` rather than
 * an `aria-label` duplicating a word already on the screen. `role="group"` is the ARIA
 * equivalent of what `<fieldset>` was providing, and a `<div>` lays out the way the canvas
 * draws.
 *
 * A group of buttons rather than a `<select>`, which is what both canvases draw — and
 * `aria-pressed` is what carries the selected state; the fill is only how it is drawn.
 */
export function ChipGroup({
  legend,
  hideLegend = false,
  value,
  options,
  disabled = false,
  onChange,
}: {
  legend: string;
  /**
   * Keep the legend for a screen reader and take it off the screen.
   *
   * The list's status chips (*Open / Finished / Everything*) are drawn with no label at all —
   * the words say what they do — but a group with no accessible name is three loose buttons to
   * anybody listening rather than reading. So the name is stated and hidden, which is a
   * different thing from inventing a label the canvas does not have. Hidden with
   * `.visually-hidden`, so it stays in the accessibility tree and out of the layout.
   */
  hideLegend?: boolean;
  value: ChipValue;
  options: readonly ChipOption[];
  /** A form or a confirmation is open — see the screens for why the chips wait for it. */
  disabled?: boolean;
  onChange: (next: ChipValue) => void;
}) {
  const labelId = useId();
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset's rendered legend is what broke this row — see the header
    <div className="whose__chips" role="group" aria-labelledby={labelId}>
      <span className={hideLegend ? "visually-hidden" : "whose__label"} id={labelId}>
        {legend}
      </span>
      {options.map((option) => (
        <button
          key={option.value}
          className={chipClass(option, value)}
          type="button"
          aria-pressed={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The two sentences that go under a filter row.
 *
 * **The first is what makes offering the control safe at all**, and it is the same sentence on
 * both screens because it corrects the same misreading: a chip labelled *Dana's* reads as a
 * **permission**, and a household could reasonably conclude that something filed under one
 * member is private to them — the opposite of the truth, and a belief this product would have
 * given them. #73 is the open spike on member-scoped visibility and it is still open, so this
 * is a correction rather than decoration.
 *
 * **The second says why the chips are frozen, in words.** Greying a control out says it is
 * unavailable and says nothing about what would make it available again — and these are only
 * ever disabled for a reason the family is in the middle of and can end in one click. Rendered
 * rather than a `title`, because a tooltip is not there for a touch screen or a screen reader.
 */
export function FilterDisclosure({ frozen }: { frozen: boolean }) {
  return (
    <>
      <p className="whose__note">
        Filtering changes what you're looking at, never what anyone can see — everyone in the family
        can read all of it.
      </p>
      {frozen ? <p className="whose__note whose__note--why">Finish or cancel that first.</p> : null}
    </>
  );
}

function chipClass(option: ChipOption, selected: ChipValue): string {
  const classes = ["whose__chip"];
  if (option.value === selected) classes.push("whose__chip--on");
  if (option.faint === true) classes.push("whose__chip--faint");
  return classes.join(" ");
}
